import React, { useCallback, useEffect, useState } from "react";
import { Route, Switch, useLocation } from "wouter";
import { useMediaQuery } from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";

import { IconButton } from "../IconButton";
import { NotificationPortal } from "../Notification";
import { DeletePopUp } from "../PopUp";
import { STORAGE_KEYS } from "../TopNavbar/tokens";
import {
  ACTIVITY_PATH,
  ActivityPage,
  ADMIN_PATH,
  AdminOverviewPage,
  INSTRUMENT_FIXES_PATH,
  INSTRUMENT_FIXES_QUEUE_LINK,
  INSTRUMENTS_PATH,
  InstrumentCatalogProvider,
  InstrumentsPage,
  JOBS_PATH,
  JobsPage,
  SCHEDULERS_PATH,
  SchedulersPage,
  TICKER_REQUESTS_PATH,
  TICKER_REQUESTS_QUEUE_LINK,
  WORK_QUEUE_PATH,
  WorkQueuePage,
  WorkQueueProvider,
} from "../../pages/Admin";
import { useLogout } from "../../hooks/useLogout";
import { QUERIES } from "../../utils/constants";
import { RedirectTo } from "../RedirectTo";
import { AdminSidebar } from "./AdminSidebar";
import {
  DrawerBackdrop,
  ShellBrand,
  ShellMain,
  ShellMobileHeader,
  ShellWrapper,
} from "./styles";
import { AdminShellProps } from "./types";

const SIDEBAR_ID = "admin-sidebar";

export const AdminShell: React.FC<AdminShellProps> = ({
  endSession,
  notifications,
  modalElement,
}) => {
  const { performLogout, isLoggingOut } = useLogout(endSession);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [location] = useLocation();
  const isMobile = useMediaQuery(QUERIES.tabletLargeAndDown);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  useEffect(closeDrawer, [closeDrawer, location, isMobile]);

  const handleLogoutClick = useCallback(() => {
    closeDrawer();
    if (localStorage.getItem(STORAGE_KEYS.skipLogoutConfirm) !== "true") {
      setShowLogoutConfirm(true);
    } else {
      performLogout();
    }
  }, [closeDrawer, performLogout]);

  const drawerBackdrop = isDrawerOpen ? (
    <DrawerBackdrop aria-hidden onClick={closeDrawer} />
  ) : null;

  return (
    <ShellWrapper>
      <DeletePopUp
        isOpen={showLogoutConfirm}
        setIsOpen={setShowLogoutConfirm}
        title="Confirm Logout"
        text="Are you sure you want to log out? You'll need to sign in again to access the admin overview."
        handleOnDelete={performLogout}
        confirmLabel="Logout"
        loadingLabel="Logging out..."
        showDontAskAgain={true}
        dontAskAgainKey={STORAGE_KEYS.skipLogoutConfirm}
        isLoading={isLoggingOut}
      />
      <NotificationPortal
        notifications={notifications}
        modalElement={modalElement}
      />
      <ShellMobileHeader inert={isDrawerOpen}>
        <ShellBrand>Net Worth Tracker · Admin</ShellBrand>
        <IconButton
          size="large"
          aria-label="Open navigation"
          aria-expanded={isDrawerOpen}
          aria-controls={SIDEBAR_ID}
          onClick={openDrawer}
        >
          <MenuRoundedIcon />
        </IconButton>
      </ShellMobileHeader>
      {drawerBackdrop}
      <AdminSidebar
        id={SIDEBAR_ID}
        isDrawerOpen={isDrawerOpen}
        isLoggingOut={isLoggingOut}
        onCloseDrawer={closeDrawer}
        onLogout={handleLogoutClick}
      />
      <ShellMain inert={isDrawerOpen}>
        <InstrumentCatalogProvider>
          <WorkQueueProvider>
            <Switch>
              <Route path={WORK_QUEUE_PATH}>
                <WorkQueuePage />
              </Route>
              <Route path={TICKER_REQUESTS_PATH}>
                <RedirectTo to={TICKER_REQUESTS_QUEUE_LINK} />
              </Route>
              <Route path={INSTRUMENT_FIXES_PATH}>
                <RedirectTo to={INSTRUMENT_FIXES_QUEUE_LINK} />
              </Route>
              <Route path={SCHEDULERS_PATH}>
                <SchedulersPage />
              </Route>
              <Route path={JOBS_PATH}>
                <JobsPage />
              </Route>
              <Route path={INSTRUMENTS_PATH}>
                <InstrumentsPage />
              </Route>
              <Route path={ACTIVITY_PATH}>
                <ActivityPage />
              </Route>
              <Route path={ADMIN_PATH}>
                <AdminOverviewPage />
              </Route>
              <Route>
                <RedirectTo to={ADMIN_PATH} />
              </Route>
            </Switch>
          </WorkQueueProvider>
        </InstrumentCatalogProvider>
      </ShellMain>
    </ShellWrapper>
  );
};
